import { apiFetch } from './api-client';
// The home onboarding gate resolves the caller's recipient via getMyRecipient, which reads the
// eventually-consistent meta-site-search index. A just-created metasite (a fresh account provisions a
// whole new site) isn't indexed for a moment — and the post-onboarding hop to the gated home is a hard
// document load (no Next basePath, so the mount-prefixed URL isn't a client route), so it hits the gate
// and, racing ahead of the index, bounces back to /onboarding/welcome. Poll the SAME read the gate uses
// (GET /api/recipient → getMyRecipient, which 404s until the recipient resolves) until it's visible, then
// let the caller navigate.
//
// Returns whether the recipient actually became visible. A caller that navigates regardless is no worse
// off than before this poll existed — the gate would bounce it, only later — but a caller whose whole
// screen is the wait needs to be able to tell a confirmed recipient from an expired deadline, or it
// hands the user to a gate that sends them straight back.
//
// The default suits a caller that navigates regardless; one that shows the user an error when the
// deadline expires wants a longer one, and passes it.
export async function waitForRecipientVisible(basePath: string, timeoutMs = 15000, intervalMs = 500): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    try {
      // Bound EACH attempt: `Date.now() < deadline` is only re-checked between awaits, so a single
      // request that never settles would hold the whole poll open past its deadline — a spinner that
      // never ends for the one caller whose screen IS this wait. Race the fetch against a short cap so a
      // stalled attempt is abandoned and the loop keeps ticking toward the deadline.
      const res = await withAttemptCap(
        apiFetch(`${basePath}/api/recipient`, { credentials: 'include' }),
        Math.min(intervalMs * 8, 4000),
      );
      if (res.ok) return true;
    } catch {
      /* transient or capped attempt — keep polling */
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  return false;
}

function withAttemptCap<T>(promise: Promise<T>, capMs: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      setTimeout(() => reject(new Error('attempt timed out')), capMs);
    }),
  ]);
}
